import { AlertCircle, AlertTriangle } from "lucide-react";
import { observer } from "mobx-react-lite";

import BlockField from "./BlockField";
import SourcesField from "./SourcesField";
import { useBlockValidationField } from "./hooks/useBlockValidationField";
import { useFieldTouch } from "./hooks/useFieldTouch";

interface ValidatedFieldProps {
  name?: string;
  path: string;
  children: React.ReactNode;
  showValidationIcons?: boolean;
  debug?: boolean;
  helpTooltip?: string;
  variant?: "block" | "sources";
}

const ValidatedField = ({
  name,
  path,
  children,
  showValidationIcons = true,
  debug = false,
  variant = "block",
  helpTooltip,
}: ValidatedFieldProps) => {
  const {
    error,
    warning,
    isValid,
    hasWarning,
    shouldShowValidation,
    markAsTouched,
    hasValidated,
    hasTriggeredSave,
    isTouched,
  } = useBlockValidationField(path);

  const { containerRef } = useFieldTouch({
    onTouch: () => {
      markAsTouched();
    },
    path,
  });

  const FieldWrapper = variant === "sources" ? SourcesField : BlockField;

  const validationContent = (
    <div className="space-y-2" ref={containerRef}>
      <div className="relative">
        {children}
        {showValidationIcons && (
          <div className="absolute right-8 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
            {error && <AlertCircle className="h-4 w-4 text-red-500 transition-colors" />}
            {!error && warning && (
              <AlertTriangle className="h-4 w-4 text-yellow-500 transition-colors" />
            )}
          </div>
        )}
      </div>

      {shouldShowValidation && error && (
        <div className="flex items-center gap-2 rounded-sm bg-red-50 px-2 py-1.5 text-xs text-red-700 transition-colors">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {shouldShowValidation && !error && warning && (
        <div className="flex items-center gap-2 rounded-sm bg-yellow-50 px-2 py-1.5 text-xs text-yellow-700 transition-colors">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span>{warning}</span>
        </div>
      )}

      {debug && (
        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <strong>Path:</strong> {path}
            </div>
            <div>
              <strong>Has Validated:</strong> {hasValidated ? "Yes" : "No"}
            </div>
            <div>
              <strong>Is Valid:</strong> {isValid ? "Yes" : "No"}
            </div>
            <div>
              <strong>Has Warning:</strong> {hasWarning ? "Yes" : "No"}
            </div>
            <div>
              <strong>Is Touched:</strong> {isTouched ? "Yes" : "No"}
            </div>
            <div>
              <strong>Save Triggered:</strong> {hasTriggeredSave ? "Yes" : "No"}
            </div>
          </div>
          {error && (
            <div className="mt-2 border-t border-gray-200 pt-2">
              <strong>Error:</strong> {error}
            </div>
          )}
          {warning && (
            <div className="mt-2 border-t border-gray-200 pt-2">
              <strong>Warning:</strong> {warning}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!name) {
    return validationContent;
  }

  return (
    <FieldWrapper helpTooltip={helpTooltip} name={name}>
      {validationContent}
    </FieldWrapper>
  );
};

export default observer(ValidatedField);
